import type { NotUndefined } from '@redux-saga/types'
import { buffers, END, eventChannel } from 'redux-saga'

import { humanizeSeconds } from '../lib/formatters'
import { Uploader } from '../lib/uploader'

const SMOOTHING_FACTOR = 0.355

const expMovAvg = () => {
  let averageSpeed = 1.0
  return (last: number) => {
    averageSpeed = SMOOTHING_FACTOR * last + (1 - SMOOTHING_FACTOR) * averageSpeed
    return averageSpeed
  }
}

const createUploadFileChannel = (
  url: string,
  payload: XMLHttpRequestBodyInit
) => {
  let fileAddedTime = 0
  const avg = expMovAvg()
  const remainingTime = (progress: number) => {
    if (!fileAddedTime) fileAddedTime = new Date().getTime()
    const timeSpent = new Date().getTime() - fileAddedTime
    return Math.round(((timeSpent / progress) - timeSpent) / 1000)
  }

  return eventChannel(emitter => {
    const onProgress = (evt: { loaded: number; total: number; lengthComputable: number}) => {
      if (evt.lengthComputable) {
        const progress = evt.loaded / evt.total
        const timeRemaining = Math.round(avg(remainingTime(progress)))
        const timeLeft = humanizeSeconds(timeRemaining)
        emitter({ progress, timeLeft })
      }
    }

    const onFailure = (errorStatusCode: number) => {
      emitter({ err: new Error(errorStatusCode.toString()) })
      emitter(END)
    }

    const onSuccess = () => {
      emitter({ success: true })
    }

    const listeners = [
      { name: 'progress', fn: onProgress },
      { name: 'error', fn: onFailure },
      { name: 'abort', fn: onFailure }
    ]

    const xhr = new Uploader()

    xhr.setListener(listeners)
    xhr.send(url, 'POST', payload, onSuccess, onFailure)

    return () => {
      xhr.removeListener(listeners)
      xhr.abort()
    }
  }, buffers.sliding<NotUndefined>(2))
}
export { createUploadFileChannel }
